import { motion } from "framer-motion"
import { forwardRef } from "react"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { fadeUp, TRANSITION } from "@/shared/lib/motion"
import type { TourStep } from "../model/tour-context"

interface TourTooltipProps {
	step: TourStep
	stepIndex: number
	totalSteps: number
	isFirst: boolean
	isLast: boolean
	top: number
	onNext: () => void
	onBack: () => void
	onSkip: () => void
}

const ACTION =
	"rounded-full px-4 py-2 text-sm font-medium transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

export const TourTooltip = forwardRef<HTMLDivElement, TourTooltipProps>(function TourTooltip(
	{ step, stepIndex, totalSteps, isFirst, isLast, top, onNext, onBack, onSkip },
	ref,
) {
	const { triggerHaptic, triggerSelection, triggerNotification } = useHaptic()

	const handleNext = () => {
		if (isLast) triggerNotification("success")
		else triggerSelection()
		onNext()
	}

	return (
		<motion.div
			ref={ref}
			key={step.key}
			layout
			className="fixed inset-x-4 z-[101] rounded-2xl bg-card p-5 shadow-2xl shadow-black/50 ring-1 ring-white/10 sm:inset-x-6"
			style={{ top }}
			variants={fadeUp}
			initial="hidden"
			animate="visible"
			exit="exit"
			transition={TRANSITION.base}
		>
			<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
				Шаг {stepIndex + 1} из {totalSteps}
			</p>
			<h3 className="mt-1.5 text-lg font-semibold tracking-tight">{step.title}</h3>
			<p className="mt-1.5 text-sm leading-snug text-muted-foreground">{step.description}</p>

			<div className="mt-4 flex items-center justify-between gap-3">
				<div className="flex items-center gap-1.5" aria-hidden>
					{Array.from({ length: totalSteps }).map((_, index) => (
						<span
							key={`${step.key}-dot-${index}`}
							className={
								index === stepIndex
									? "h-1.5 w-4 rounded-full bg-primary transition-all"
									: "h-1.5 w-1.5 rounded-full bg-white/20 transition-all"
							}
						/>
					))}
				</div>

				<div className="flex items-center gap-1">
					{isFirst ? (
						<button
							type="button"
							onClick={() => {
								triggerHaptic("light")
								onSkip()
							}}
							className={`${ACTION} text-muted-foreground`}
						>
							Пропустить
						</button>
					) : (
						<button
							type="button"
							onClick={() => {
								triggerSelection()
								onBack()
							}}
							className={`${ACTION} text-muted-foreground`}
						>
							Назад
						</button>
					)}
					<button
						type="button"
						onClick={handleNext}
						className={`${ACTION} bg-primary text-primary-foreground shadow-lg shadow-primary/25`}
					>
						{isLast ? "Понятно" : "Далее"}
					</button>
				</div>
			</div>
		</motion.div>
	)
})
